import React, { useState } from 'react';
import {
  TextField, FormControl, FormControlLabel, Radio, RadioGroup,
  Checkbox, Select, MenuItem, FormGroup, FormHelperText,
  Rating, Switch, Typography, Box, Button, OutlinedInput,
  InputAdornment, IconButton, Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

/**
 * FieldLabel
 * ----------
 * Plain text label rendered above the input — not inside it.
 */
const FieldLabel = ({ label, required, error, isUnique }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
    <Typography
      component="label"
      sx={{
        fontWeight: 600,
        fontSize: '1rem',
        color: error ? 'error.main' : 'text.primary',
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
      )}
    </Typography>
    {isUnique && (
      <Chip label="unique" size="small" variant="outlined" color="primary"
        sx={{ fontSize: 10, height: 18, '& .MuiChip-label': { px: 0.75 } }}
      />
    )}
  </Box>
);

const DynamicField = ({ field, value, onChange, error }) => {
  const {
    id, type, label, placeholder, required, disabled,
    options = [], validation = {}, helperText, isUnique,
  } = field;

  const [showPassword, setShowPassword] = useState(false);

  const inputProps = {
    fullWidth: true,
    placeholder,
    required,
    disabled,
    error: !!error,
    size: 'medium',
  };

  const helper = error || helperText || '';

  // ── Text-like inputs ────────────────────────────────────────────────────────
  if (['text', 'email', 'tel', 'url', 'number'].includes(type)) {
    return (
      <Box>
        <FieldLabel label={label} required={required} error={!!error} isUnique={isUnique} />
        <TextField
          {...inputProps}
          type={type}
          value={value || ''}
          onChange={(e) => onChange(id, e.target.value)}
          helperText={helper}
          inputProps={{
            minLength: validation.minLength,
            maxLength: validation.maxLength,
            min:       validation.min,
            max:       validation.max,
          }}
        />
      </Box>
    );
  }

  // ── Password — with show/hide toggle ─────────────────────────────────────
  if (type === 'password') {
    return (
      <Box>
        <FieldLabel label={label} required={required} error={!!error} isUnique={isUnique} />
        <TextField
          {...inputProps}
          type={showPassword ? 'text' : 'password'}
          value={value || ''}
          onChange={(e) => onChange(id, e.target.value)}
          helperText={helper}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setShowPassword(s => !s)}
                  edge="end"
                  tabIndex={-1}
                >
                  {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          inputProps={{
            minLength: validation.minLength,
            maxLength: validation.maxLength,
          }}
        />
      </Box>
    );
  }

  // ── Textarea ────────────────────────────────────────────────────────────────
  if (type === 'textarea') {
    return (
      <Box>
        <FieldLabel label={label} required={required} error={!!error} isUnique={isUnique} />
        <TextField
          {...inputProps}
          multiline
          minRows={3}
          value={value || ''}
          onChange={(e) => onChange(id, e.target.value)}
          helperText={helper}
        />
      </Box>
    );
  }

  // ── Date / time ─────────────────────────────────────────────────────────────
  if (['date', 'datetime', 'time'].includes(type)) {
    return (
      <Box>
        <FieldLabel label={label} required={required} error={!!error} isUnique={isUnique} />
        <TextField
          {...inputProps}
          type={type === 'datetime' ? 'datetime-local' : type}
          value={value || ''}
          onChange={(e) => onChange(id, e.target.value)}
          helperText={helper}
          InputLabelProps={{ shrink: true }}
        />
      </Box>
    );
  }

  // ── Select / dropdown ───────────────────────────────────────────────────────
  if (type === 'select') {
    return (
      <Box>
        <FieldLabel label={label} required={required} error={!!error} isUnique={isUnique} />
        <FormControl fullWidth size="small" error={!!error} disabled={disabled}>
          <Select
            value={value || ''}
            displayEmpty
            input={<OutlinedInput />}
            onChange={(e) => onChange(id, e.target.value)}
            renderValue={(v) =>
              v
                ? options.find((o) => o.value === v)?.label || v
                : <span style={{ color: '#9e9e9e' }}>{placeholder || 'Select an option'}</span>
            }
          >
            {placeholder && (
              <MenuItem value="" disabled><em>{placeholder}</em></MenuItem>
            )}
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
          {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
      </Box>
    );
  }

  // ── Radio group ─────────────────────────────────────────────────────────────
  if (type === 'radio') {
    return (
      <Box>
        <FieldLabel label={label} required={required} error={!!error} isUnique={isUnique} />
        <FormControl error={!!error} disabled={disabled}>
          <RadioGroup value={value || ''} onChange={(e) => onChange(id, e.target.value)}>
            {options.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio />}
                label={<Typography sx={{ fontSize: '1rem' }}>{opt.label}</Typography>}
              />
            ))}
          </RadioGroup>
          {helper && <FormHelperText sx={{ fontSize: '0.875rem' }}>{helper}</FormHelperText>}
        </FormControl>
      </Box>
    );
  }

  // ── Checkbox group ──────────────────────────────────────────────────────────
  if (type === 'checkbox') {
    const checked = Array.isArray(value) ? value : [];
    return (
      <Box>
        <FieldLabel label={label} required={required} error={!!error} isUnique={isUnique} />
        <FormControl error={!!error} disabled={disabled}>
          <FormGroup>
            {options.map((opt) => (
              <FormControlLabel
                key={opt.value}
                control={
                  <Checkbox
                    checked={checked.includes(opt.value)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...checked, opt.value]
                        : checked.filter((v) => v !== opt.value);
                      onChange(id, next);
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: '1rem' }}>{opt.label}</Typography>}
              />
            ))}
          </FormGroup>
          {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
      </Box>
    );
  }

  // ── Toggle / switch ─────────────────────────────────────────────────────────
  if (type === 'toggle') {
    return (
      <Box>
        <FieldLabel label={label} required={required} error={!!error} isUnique={isUnique} />
        <FormControl error={!!error} disabled={disabled}>
          <FormControlLabel
            control={<Switch checked={!!value} onChange={(e) => onChange(id, e.target.checked)} />}
            label={<Typography variant="body2" color="text.secondary">{placeholder || ''}</Typography>}
          />
          {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
      </Box>
    );
  }

  // ── Star rating ─────────────────────────────────────────────────────────────
  if (type === 'rating') {
    return (
      <Box>
        <FieldLabel label={label} required={required} error={!!error} isUnique={isUnique} />
        {placeholder && (
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            {placeholder}
          </Typography>
        )}
        <Rating value={Number(value) || 0} onChange={(_, val) => onChange(id, val)} />
        {helper && <FormHelperText error={!!error}>{helper}</FormHelperText>}
      </Box>
    );
  }

  // ── File / image upload ─────────────────────────────────────────────────────
  if (type === 'file' || type === 'image') {
    return (
      <Box>
        <FieldLabel label={label} required={required} error={!!error} isUnique={isUnique} />
        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUploadIcon />}
          size="small"
          color={error ? 'error' : 'primary'}
          fullWidth
          sx={{ justifyContent: 'flex-start', py: 1.5 }}
        >
          {value
            ? (typeof value === 'string' ? value : value.name)
            : (placeholder || `Upload ${type === 'image' ? 'image' : 'file'}`)}
          <input
            type="file"
            hidden
            accept={validation.accept || (type === 'image' ? 'image/*' : '*')}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) onChange(id, file);
            }}
          />
        </Button>
        {helper && <FormHelperText error={!!error}>{helper}</FormHelperText>}
      </Box>
    );
  }

  // ── Hidden ──────────────────────────────────────────────────────────────────
  if (type === 'hidden') return null;

  // ── Display-only: divider ────────────────────────────────────────────────────
  if (type === 'divider') {
    return <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 1 }} />;
  }

  // ── Display-only: heading ────────────────────────────────────────────────────
  if (type === 'heading') {
    return (
      <Typography variant="h6" fontWeight={700} sx={{ mt: 1, mb: 0.5, color: 'text.primary' }}>
        {label}
      </Typography>
    );
  }

  // ── Display-only: paragraph ─────────────────────────────────────────────────
  if (type === 'paragraph') {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
        {label || placeholder}
      </Typography>
    );
  }

  // ── Composite types (handled by FieldsStep, not here) ───────────────────────
  // column_group and selection_group are rendered by FieldsStep → FieldRenderer.
  // DynamicField should never receive them, but guard defensively.
  if (type === 'column_group' || type === 'selection_group') return null;

  // ── Fallback ────────────────────────────────────────────────────────────────
  return (
    <Box>
      <FieldLabel label={label} required={required} error={!!error} isUnique={isUnique} />
      <TextField
        {...inputProps}
        value={value || ''}
        onChange={(e) => onChange(id, e.target.value)}
        helperText={helper}
      />
    </Box>
  );
};

export default DynamicField;